/* ═══════════════════════ AI — chat + tool-use ═══════════════════════ */

import { S } from './state.js';
import { esc, wbox } from './utils.js';
import { QUICK_PROMPTS, SYSTEM_PROMPT, DESTS, DAYS, CHKLST, PACKING, HOTEL_INFO, nowContext } from './data.js';
import { renderPage } from './main.js';
import { getPreAnswer } from './preAnswers.js';
import { getDocsForAI } from './docs.js'; // kun brukt i executeTool (runtime) — trygt tross sirkulær graf

/* ── Tools: AI kan kalle disse for å hente faktisk reise-state ── */
export const TOOLS = [
  {
    name: "get_trip_status",
    description: "Henter brukerens nåværende reisestatus: hvor mange dager til/siden avreise, hva som skjer på dagens program akkurat nå, og fremdrift på sjekkliste/pakkeliste. Bruk denne når brukeren spør om 'nå', 'i dag', 'hvor langt er jeg' eller status.",
    input_schema: { type: "object", properties: {}, required: [] }
  },
  {
    name: "get_user_data",
    description: "Henter brukerens egne data: planlagte aktiviteter ('Mine'), budsjett-totaler i CAD og NOK, og siste 5 dagbok-innlegg (kun titler og forfattere). Bruk når brukeren spør om sine egne planer, utgifter eller dagbok.",
    input_schema: { type: "object", properties: {}, required: [] }
  },
  {
    name: "get_weather",
    description: "Henter værmelding for en lokasjon på reisen. Bruk når brukeren spør om vær.",
    input_schema: {
      type: "object",
      properties: {
        location: { type: "string", description: "Lokasjon: vancouver, juneau, skagway, ketchikan, banff, calgary" }
      },
      required: ["location"]
    }
  },
  {
    name: "get_live_rates",
    description: "Henter live valutakurser CAD-NOK, USD-NOK, CAD-USD. Bruk når brukeren spør om dagens kurs eller vil regne om beløp.",
    input_schema: { type: "object", properties: {}, required: [] }
  },
  {
    name: "get_itinerary",
    description: "Henter det komplette dag-for-dag-programmet for hele reisen (alle 18 dager): klokkeslett, aktiviteter, henteadresser (f.eks. hvor leiebilen hentes og leveres), hoteller med adresse/telefon/referanse, og Nicholas' avvikende opplegg. Bruk denne når brukeren spør HVA, HVOR eller NÅR noe skjer på en bestemt dag — svar aldri «jeg vet ikke» om programmet uten å ha sjekket her.",
    input_schema: {
      type: "object",
      properties: {
        day: { type: "string", description: "Valgfritt: dato («26. jul») eller reisedagnummer («10»). Utelat for hele programmet." }
      },
      required: []
    }
  },
  {
    name: "get_documents",
    description: "Henter innholdet i brukerens opplastede reisedokumenter fra det private arkivet: ordrebekreftelser, flybilletter, cruisedokumenter (Guest Ticket), bagasjelapper, forsikring, eTA m.m. Bruk denne når brukeren spør om noe som kan stå i et dokument — referansenumre, avgangs-/innsjekkingstider, seter, lugar, adresser, bagasjeregler, vilkår, frister, nødtelefoner — i stedet for å gjette. Arkivet er større enn ett svar: bruk `query` med de mest distinktive ordene (leverandørnavn, referansenummer, gatenavn) for å få utdrag rundt treffene, og `labels` for å hente et bestemt dokument i sin helhet. Svaret inneholder alltid `index` (alle dokumenter som finnes) og `omitted` (de som ikke ble tatt med) — sjekk disse og kall verktøyet på nytt før du konkluderer med at noe ikke står i dokumentene. Krever at brukeren er innlogget i dokumentarkivet.",
    input_schema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Fritekst-søk i dokumentene, f.eks. «AVIS henting Hornby». Returnerer utdrag rundt treffene fra hele dokumentet — også siste side." },
        labels: { type: "array", items: { type: "string" }, description: "Hent bestemte dokumenter i sin helhet. Delvis navn holder, f.eks. [\"Ordrebekreftelse\"]." }
      },
      required: []
    }
  }
];

const DEPARTURE_AI = new Date("2026-07-17T13:50:00+02:00");
const RETURN_AI    = new Date("2026-08-03T15:25:00+02:00");
function tripDayNow(){
  const now=new Date();
  if(now<DEPARTURE_AI||now>RETURN_AI) return -1;
  return Math.floor((now-DEPARTURE_AI)/86400000);
}

export async function executeTool(name, input){
  try{
    if(name === "get_trip_status"){
      const day=tripDayNow();
      const now=new Date();
      const daysToDep = Math.ceil((DEPARTURE_AI - now) / 86400000);
      const chkDone = Object.values(S.chk||{}).filter(Boolean).length;
      const chkTotal = CHKLST.reduce((s,g)=>s+g.items.length,0);
      const pakkDone = Object.values(S.packChk||{}).filter(Boolean).length;
      const pakkTotal = PACKING.reduce((s,g)=>s+g.items.length,0);
      const out = {
        currentLocalTime: now.toISOString(),
        tripStatus: day < 0 ? (now < DEPARTURE_AI ? "før reise" : "etter reise") : "på reise",
        daysUntilDeparture: day < 0 && now < DEPARTURE_AI ? daysToDep : null,
        currentTripDay: day >= 0 ? day + 1 : null,
        totalTripDays: 18,
        currentDayProgram: day >= 0 && day < DAYS.length ? {
          title: DAYS[day].title,
          em: DAYS[day].em,
          phase: DAYS[day].phase,
          items: DAYS[day].items
        } : null,
        checklistProgress: `${chkDone}/${chkTotal} (${Math.round(chkDone/chkTotal*100)}%)`,
        packingProgress: `${pakkDone}/${pakkTotal} (${Math.round(pakkDone/pakkTotal*100)}%)`,
      };
      return out;
    }
    if(name === "get_user_data"){
      const totalCad = (S.budgetEntries||[]).reduce((s,e)=>s+(e.amtCad||0),0);
      const totalNok = (S.budgetEntries||[]).reduce((s,e)=>s+(e.amtNok||0),0);
      return {
        plannedActivities: (S.acts||[]).map(a=>({
          name:a.name, destination:a.destination, status:a.status, date:a.date, priceCad:a.priceCad
        })),
        budget: {
          target_cad: S.budgetTarget,
          spent_cad: totalCad,
          spent_nok: totalNok,
          entries_count: (S.budgetEntries||[]).length,
        },
        recentDiaryEntries: (S.diary||[]).slice(0,5).map(d=>({
          title:d.title, author:d.author, dayIdx:d.dayIdx, date:new Date(d.ts).toISOString().slice(0,10)
        })),
      };
    }
    if(name === "get_weather"){
      // /api/weather krever lat/lng — slå opp kjente reisemål til koordinater.
      const WX_COORDS = {
        vancouver:{lat:49.28,lng:-123.12}, juneau:{lat:58.30,lng:-134.42},
        skagway:{lat:59.46,lng:-135.31},   ketchikan:{lat:55.34,lng:-131.65},
        kamloops:{lat:50.67,lng:-120.33},  jasper:{lat:52.87,lng:-118.08},
        banff:{lat:51.18,lng:-115.57},     calgary:{lat:51.05,lng:-114.07},
      };
      const loc = String(input?.location||"vancouver").toLowerCase().trim();
      const c = WX_COORDS[loc] || WX_COORDS.vancouver;
      const res = await fetch(`/api/weather?lat=${c.lat}&lng=${c.lng}`);
      if(!res.ok) return { error: "Værendpoint ikke tilgjengelig" };
      return { location: loc, ...(await res.json()) };
    }
    if(name === "get_live_rates"){
      const res = await fetch("/api/rates");
      if(!res.ok) return { error: "Kursendpoint ikke tilgjengelig" };
      return await res.json();
    }
    if(name === "get_itinerary"){
      // Hele programmet ligger i appen (DAYS) — uten dette verktøyet så
      // assistenten bare DAGENS plan via nowContext, og svarte «vet ikke» på
      // spørsmål om andre dager (f.eks. hvor leiebilen hentes 26. juli).
      const days = DAYS.map((d,i)=>({
        dayNumber: i+1, date: d.d, weekday: d.wd, title: d.title, phase: d.phase,
        program: (d.items||[]).map(it=>`${it.t} — ${it.v}${it.s?` (${it.s})`:""}`),
        hotel: d.hotel ? {
          name: d.hotel, address: d.hadr||null, phone: d.htel||null,
          ref: d.href||null, note: d.hci||null
        } : null,
        nicholas: d.split ? { title: d.split.title, lines: d.split.lines, note: d.split.foot } : null,
      }));
      const want = String(input?.day||"").trim().toLowerCase();
      const hit = want
        ? days.filter(d=>d.date.toLowerCase().includes(want)||String(d.dayNumber)===want)
        : days;
      return { totalDays: days.length, days: hit.length?hit:days };
    }
    if(name === "get_documents"){
      return await getDocsForAI(input || {});
    }
    return { error: "Ukjent tool: "+name };
  } catch(e){
    return { error: e.message || "Tool feilet" };
  }
}

/* ── 7-dagers cache i localStorage ── */
function aiCacheKey(q){let h=0;for(let i=0;i<q.length;i++){h=((h<<5)-h)+q.charCodeAt(i);h|=0;}return"aic_"+h;}
function aiCacheGet(q){try{const d=localStorage.getItem(aiCacheKey(q));if(d){const p=JSON.parse(d);if(Date.now()-p.ts<7*86400000)return p.v;}return null;}catch{return null;}}
function aiCacheSet(q,v){try{const keys=Object.keys(localStorage).filter(k=>k.startsWith("aic_"));if(keys.length>50){keys.slice(0,10).forEach(k=>localStorage.removeItem(k));}localStorage.setItem(aiCacheKey(q),JSON.stringify({v,ts:Date.now()}));}catch{}}

/* ── Chat-rendering ── */
// Markdown-parser for AI-svar:
//   ## Tittel        → <h4>
//   **bold**         → <strong>
//   *italic*         → <em>
//   - [Q] spørsmål   → klikkbar knapp (sendCustom)
//   - vanlig         → <li>
//   blank linje      → ny seksjon
//   Newlines i tekst → <br>
export function fmtAI(txt){
  const lines = String(txt || '').split('\n');
  let html = '';
  let inList = false;
  let inFollow = false;
  let deepenIdx = null;
  const followUps = [];

  const closeList = () => { if(inList){ html += '</ul>'; inList = false; } };
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  for(let raw of lines){
    const line = raw.trim();

    // [DEEPEN:N] marker — pre-answer som tillater Claude-fordypning
    const dMatch = line.match(/^\[DEEPEN:(\d+)\]$/);
    if(dMatch){
      deepenIdx = Number(dMatch[1]);
      continue;
    }

    // Oppfølgings-spørsmål: «- [Q] tekst»
    const qMatch = line.match(/^[-*]\s*\[Q\]\s+(.+)$/);
    if(qMatch){
      followUps.push(qMatch[1]);
      continue;
    }

    if(line === '## Oppfølging' || /^##\s+Oppfølging/i.test(line)){
      // Vi samler [Q]-linjene under, renderes på slutten
      inFollow = true;
      closeList();
      continue;
    }

    if(/^##\s+/.test(line)){
      closeList();
      html += '<h4 class="ai-h">' + inline(line.replace(/^##\s+/, '')) + '</h4>';
      continue;
    }

    if(/^[-*]\s+/.test(line)){
      if(!inList){ html += '<ul class="ai-ul">'; inList = true; }
      html += '<li>' + inline(line.replace(/^[-*]\s+/, '')) + '</li>';
      continue;
    }

    if(line === ''){
      closeList();
      continue;
    }

    // Numerert liste: «1. tekst»
    if(/^\d+\.\s+/.test(line)){
      closeList();
      html += '<p class="ai-p ai-num"><strong>' + line.match(/^(\d+)\./)[1] + '.</strong> ' + inline(line.replace(/^\d+\.\s+/, '')) + '</p>';
      continue;
    }

    closeList();
    html += '<p class="ai-p">' + inline(line) + '</p>';
  }
  closeList();

  // Render oppfølgingsspørsmål som knapper hvis vi har noen
  if(followUps.length){
    html += '<div class="ai-followup-hdr">Spør videre</div>';
    html += '<div class="ai-followup-list">';
    for(const q of followUps){
      const safe = q.replace(/'/g, '&#39;').replace(/"/g, '&quot;');
      html += `<button class="ai-followup-btn" onclick="window.sendCustomAi(&quot;${safe}&quot;)">${inline(q)}</button>`;
    }
    html += '</div>';
  }

  // «Spør Claude om dypere svar»-CTA for pre-answers
  if(deepenIdx != null){
    html += `<button class="ai-deepen" onclick="window.askClaudeDeeper(${deepenIdx})">Spør Claude om dypere svar →</button>`;
  }

  return html;
}

// Sender et nytt spørsmål til Claude — eksponert for klikkbare oppfølgings-knapper
if(typeof window !== 'undefined'){
  window.sendCustomAi = function(q){
    const inp = document.getElementById('chat-in');
    if(inp) inp.value = q;
    sendChat();
  };
}
export function bindAI(){const el=document.getElementById("chat-msgs");if(el)el.scrollTop=el.scrollHeight;}
export function chatKey(e){if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendChat();}}
export function sendQuick(i){
  // Sjekk forhåndsbesvart svar først — sparer Claude-kall + virker offline
  const pre = getPreAnswer(i);
  if(pre){
    const q = QUICK_PROMPTS[i].q;
    S.chatMsgs.push({ role: 'user', content: q });
    // Legg til «Spør Claude om dypere svar» som siste linje i pre-answer
    // før «## Oppfølging». Det rendres som CTA-knapp i fmtAI.
    const withDeepen = pre + '\n\n[DEEPEN:' + i + ']';
    S.chatMsgs.push({ role: 'ai', content: withDeepen, preIdx: i });
    renderPage();
    return;
  }
  const inp = document.getElementById("chat-in");
  if(inp) inp.value = QUICK_PROMPTS[i].q;
  sendChat();
}

// Bes om dypere Claude-svar på et pre-besvart prompt
if(typeof window !== 'undefined'){
  window.askClaudeDeeper = function(promptIdx){
    const q = QUICK_PROMPTS[promptIdx]?.q;
    if(!q) return;
    // Sender et utvidet spørsmål til Claude
    const deeperQ = q + ' Bygg videre på det jeg allerede har lest om dette — gå dypere i detalj, gi konkrete tall og ferske detaljer.';
    const inp = document.getElementById('chat-in');
    if(inp) inp.value = deeperQ;
    sendChat();
  };
}

export function renderAI(){
  const online=navigator.onLine;
  const hasMessages=S.chatMsgs.length>1;
  return`
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:2px">
    <div class="page-title" style="margin-bottom:0">✨ Reiseassistent</div>
    <div class="conn"><span class="conn-dot ${online?"conn-on":"conn-off"}"></span><span style="color:${online?"var(--green)":"var(--t3)"};font-size:11px">${online?"Online":"Offline"}</span></div>
  </div>
  <div style="font-size:13px;color:var(--t2);margin-bottom:14px">Spør om hva som helst – høydepunkter, mat, aktiviteter, praktisk info</div>
  ${!online?wbox("wred","📡 Ingen internettforbindelse. AI-assistenten krever nett."):""}

  <div style="margin-bottom:16px">
    <div class="chat-row">
      <textarea id="chat-in" class="chat-input" placeholder="${online?"Hva lurer du på om turen?":"Ikke tilgjengelig offline"}" ${!online||S.chatLoading?"disabled":""} rows="1" onkeydown="chatKey(event)"></textarea>
      <button class="chat-send" id="chat-send" onclick="sendChat()" ${!online||S.chatLoading?"disabled":""} aria-label="Send melding">↑</button>
    </div>
    <div style="font-size:10.5px;color:var(--t3);margin-top:5px;text-align:center">Enter = send · Shift+Enter = ny linje</div>
  </div>

  ${!hasMessages?`
  ${[{cat:"highlights",label:"✨ Høydepunkter"},{cat:"cruise",label:"🚢 Cruise & Alaska"},{cat:"mountains",label:"🏔️ Rocky Mountains"},{cat:"praktisk",label:"🧭 Praktisk"}].map(g=>{
    const items=QUICK_PROMPTS.filter(p=>p.cat===g.cat);
    return items.length?`
    <div style="font-size:11px;font-weight:700;color:var(--t3);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px;margin-top:12px">${g.label}</div>
    <div class="quick-grid">
      ${items.map((p,j)=>{
        const idx=QUICK_PROMPTS.indexOf(p);
        const hasPre=!!getPreAnswer(idx);
        // Forhåndsbesvarte fungerer offline; resten krever nett
        const isDisabled = !hasPre && !online;
        return`<button class="qbtn${hasPre?" qbtn-pre":""}" onclick="sendQuick(${idx})" ${isDisabled?"disabled":""}>
        <span style="font-size:16px;margin-right:4px">${p.i}</span>${esc(p.t)}
        ${hasPre?'<span class="qbtn-badge" title="Forhåndssvar – fungerer offline">★</span>':''}
      </button>`}).join("")}
    </div>`:"";
  }).join("")}`:`
  <div class="chat-wrap">
    <div class="chat-msgs" id="chat-msgs">
      ${S.chatMsgs.map(m=>`<div style="display:flex;flex-direction:column;${m.role==="ai"?"align-items:flex-start":"align-items:flex-end"}">
        <div class="bubble-label ${m.role==="ai"?"lbl-ai":"lbl-user"}">${m.role==="ai"?"✨ AI Reiseassistent":"Du"}</div>
        <div class="bubble ${m.role==="ai"?"ai":"user"}">${m.role==="ai"?fmtAI(m.content):esc(m.content)}</div>
      </div>`).join("")}
      ${S.chatLoading?`<div class="bubble ai" style="padding:12px 16px"><span class="dot"></span> <span class="dot"></span> <span class="dot"></span></div>`:""}
    </div>
    <div style="text-align:center;padding:8px 0"><button onclick="S.chatMsgs=[{role:'ai',content:'Hei! Spør meg om hva som helst om turen. 🍁'}];renderPage()" style="background:none;border:none;color:var(--t3);font-size:11px;cursor:pointer;font-family:var(--font);text-decoration:underline">Tøm samtale og vis inspirasjonskort</button></div>
  </div>`}`;
}

/**
 * Streamer ett API-kall og parser ut content blocks (text + tool_use)
 * pluss stop_reason. Skriver text-deltaer direkte til onText for live-UI.
 */
export async function streamOnce(messages, system, tools, onText){
  const res = await fetch("/api/chat",{
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({system: (system||"") + nowContext(), messages, tools})
  });
  if(!res.ok){ const err=await res.text(); throw new Error("API "+res.status+": "+err.slice(0,200)); }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  const blocks = []; // index → { type, ...fields, _partialInput? }
  let stopReason = null;

  while(true){
    const {done, value} = await reader.read();
    if(done) break;
    buf += decoder.decode(value,{stream:true});
    const lines = buf.split("\n");
    buf = lines.pop() || "";
    for(const line of lines){
      if(!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if(!json || json === "[DONE]") continue;
      let evt;
      try { evt = JSON.parse(json); } catch { continue; }
      switch(evt.type){
        case "content_block_start":
          blocks[evt.index] = { ...evt.content_block, _partialInput: "" };
          if(blocks[evt.index].type === "text") blocks[evt.index].text = blocks[evt.index].text || "";
          break;
        case "content_block_delta": {
          const b = blocks[evt.index];
          if(!b) break;
          if(evt.delta?.type === "text_delta"){
            b.text = (b.text||"") + evt.delta.text;
            onText?.(evt.delta.text);
          } else if(evt.delta?.type === "input_json_delta"){
            b._partialInput += evt.delta.partial_json || "";
          }
          break;
        }
        case "content_block_stop": {
          const b = blocks[evt.index];
          if(b && b.type === "tool_use" && b._partialInput){
            try { b.input = JSON.parse(b._partialInput); } catch { b.input = {}; }
            delete b._partialInput;
          }
          break;
        }
        case "message_delta":
          if(evt.delta?.stop_reason) stopReason = evt.delta.stop_reason;
          break;
      }
    }
  }
  // Strip private fields
  const cleanBlocks = blocks.filter(Boolean).map(b => { const c={...b}; delete c._partialInput; return c; });
  return { blocks: cleanBlocks, stopReason };
}

export async function sendChat(){
  const inp=document.getElementById("chat-in");
  const q=(inp?inp.value:"").trim();
  if(!q||S.chatLoading)return;
  if(inp)inp.value="";

  // Offline: bruk cache hvis vi har den
  if(!navigator.onLine){
    const cached=aiCacheGet(q);
    S.chatMsgs.push({role:"user",content:q});
    S.chatMsgs.push({role:"ai",content: cached || "📡 Du er offline, og jeg har ikke et bufret svar for dette spørsmålet."});
    renderPage();return;
  }

  S.chatMsgs.push({role:"user",content:q});
  S.chatLoading=true;
  S.chatMsgs.push({role:"ai",content:""}); // placeholder for streaming
  const aiIdx = S.chatMsgs.length-1;
  renderPage();

  // Bygg API-historikk fra UI-meldinger (skip placeholder)
  let apiMessages = S.chatMsgs.slice(0,-1).map(m=>({
    role: m.role==="ai" ? "assistant" : "user",
    content: m.content
  }));

  const onTextDelta = (delta) => {
    S.chatMsgs[aiIdx].content += delta;
    const el = document.getElementById("chat-msgs");
    if(el){
      const bubbles = el.querySelectorAll(".bubble.ai");
      const last = bubbles[bubbles.length-1];
      if(last) last.innerHTML = fmtAI(S.chatMsgs[aiIdx].content);
      el.scrollTop = el.scrollHeight;
    }
  };

  try {
    let iterations = 0;
    while(iterations++ < 5){ // sikkerhetsgrense mot tool-loops
      const { blocks, stopReason } = await streamOnce(apiMessages, SYSTEM_PROMPT, TOOLS, onTextDelta);

      if(stopReason !== "tool_use"){
        // Ferdig — vi har final tekst
        if(S.chatMsgs[aiIdx].content) aiCacheSet(q, S.chatMsgs[aiIdx].content);
        break;
      }

      // Det var tool-bruk: legg til assistant-blokken som inneholder tool_use,
      // kjør tools, og legg ved resultat som user-melding
      apiMessages.push({ role:"assistant", content: blocks });
      const toolResults = [];
      for(const b of blocks){
        if(b.type !== "tool_use") continue;
        const result = await executeTool(b.name, b.input || {});
        toolResults.push({
          type: "tool_result",
          tool_use_id: b.id,
          content: typeof result === "string" ? result : JSON.stringify(result)
        });
      }
      apiMessages.push({ role:"user", content: toolResults });
      // Loopen fortsetter; ny streamOnce vil produsere nytt svar
    }
  } catch(e){
    S.chatMsgs[aiIdx].content = "Kunne ikke koble til: "+(e.message||"Sjekk nettforbindelsen. 📡");
  }
  S.chatLoading=false;
  renderPage();
}

/* ── Destinasjon-AI ── */
export function destAiQuick(di,qi){
  const dest=DESTS[di];if(!dest)return;
  const qs=["Restaurant i "+dest.name,"Vandring i "+dest.name,"Barnevennlig i "+dest.name,"Gratis i "+dest.name,"Naturopplevelser "+dest.name,"Shopping "+dest.name];
  const q=qs[qi]||"";
  const inp=document.getElementById("dest-ai-input");
  if(inp)inp.value=q;
  destAiSend(di);
}

export async function destAiSend(di){
  const inp=document.getElementById("dest-ai-input");
  const q=(inp?inp.value:"").trim();
  if(!q||S.destAiLoading)return;
  if(inp)inp.value="";
  const dest=DESTS[di];
  if(!S.destAiMsgs)S.destAiMsgs=[];
  S.destAiMsgs.push({role:"user",content:q});
  S.destAiLoading=true;
  const container=document.getElementById("dest-ai-msgs");
  if(container){
    container.style.marginTop="10px";
    container.innerHTML=S.destAiMsgs.map(m=>`<div style="padding:10px 13px;border-radius:12px;font-size:13px;line-height:1.65;margin-bottom:7px;${m.role==="user"?"background:var(--gold);color:#080f1a;font-weight:500":"background:var(--bg3);border:1px solid var(--border);color:var(--t1)"}">${m.role==="ai"?fmtAI(m.content):esc(m.content)}</div>`).join("")+
      `<div style="padding:12px;border-radius:12px;background:var(--bg3);border:1px solid var(--border)"><span class="dot"></span> <span class="dot"></span> <span class="dot"></span></div>`;
    container.scrollTop=container.scrollHeight;
  }
  const sendBtn=document.getElementById("dest-ai-input")?.nextElementSibling;
  if(sendBtn)sendBtn.textContent="⏳";

  const destContext=`Jeg er på tur til ${dest?.name||"destinasjonen"} (${dest?.dates||""}) som del av en ferie i Canada og Alaska. ${dest?.desc||""} Hva anbefaler du?`;
  const sysLocal=`Du er reiseekspert for Canada og Alaska. Brukeren er på tur til ${dest?.name}. Gi konkrete, korte anbefalinger med stedsnavn og praktiske tips. Svar på norsk.`;
  const hist=S.destAiMsgs.slice(0,-1).map(m=>({role:m.role==="ai"?"assistant":"user",content:m.content}));
  try{
    const res=await fetch("/api/dest-chat",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({system:sysLocal,
        messages:[{role:"user",content:destContext},...hist,{role:"user",content:q}]})});
    const data=await res.json();
    S.destAiMsgs.push({role:"ai",content:data.content?.[0]?.text||"Beklager, noe gikk galt."});
  }catch{
    S.destAiMsgs.push({role:"ai",content:"Kunne ikke koble til. Sjekk nettforbindelsen. 📡"});
  }
  S.destAiLoading=false;
  if(container){
    container.innerHTML=S.destAiMsgs.map(m=>`<div style="padding:10px 13px;border-radius:12px;font-size:13px;line-height:1.65;margin-bottom:7px;${m.role==="user"?"background:var(--gold);color:#080f1a;font-weight:500":"background:var(--bg3);border:1px solid var(--border);color:var(--t1)"}">${m.role==="ai"?fmtAI(m.content):esc(m.content)}</div>`).join("");
    container.scrollTop=container.scrollHeight;
  }
  if(sendBtn)sendBtn.textContent="↑";
}
